import { useEffect, useState } from 'react'
import { Pencil, RefreshCw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Drawer, DrawerField, DrawerSection } from '@/shared/ui/Drawer'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { fmtDate, fmtDateTime } from '@/shared/lib/format'
import { explain } from '@/shared/api/problem'
import type { ProgramResponse } from '@/shared/api/types'
import { changeProgramStatus, getProgram } from './api'
import { ProgramFormDialog } from './ProgramFormDialog'
import { Money, PlannedChip } from './parts'
import {
  CategoryBadge, ChannelBadge, ProgramStatusBadge,
  STATUS_TRANSITIONS, programStatusLabel,
} from './vocab'
import { TargetingPanel } from './panels/TargetingPanel'
import { BeneficiariesPanel } from './panels/BeneficiariesPanel'
import { MilestonesPanel } from './panels/MilestonesPanel'
import { KpisPanel } from './panels/KpisPanel'
import { MoneyPanel } from './panels/MoneyPanel'
import { ImpactPanel } from './panels/ImpactPanel'
import { FunnelPanel } from './panels/FunnelPanel'
import { DocumentsPanel } from './panels/DocumentsPanel'
import { PartnersPanel } from './panels/PartnersPanel'
import { ConnectionsPanel } from './panels/ConnectionsPanel'
import { ProgramCoveragePanel } from './panels/CoveragePanel'
import { ProgramAuditPanel } from './panels/ProgramAuditPanel'

/**
 * The programme drawer — twelve tabs, matching the shape PollSphere established
 * because §2a of the root plan says PollSphere IS the UI, ported rather than
 * reinterpreted.
 *
 * What is NOT ported is how it saved. PollSphere's drawer held the whole
 * aggregate — milestones, KPIs, documents — in one form and PATCHed it back as
 * an array, so every edit deleted and recreated every child row and every id
 * changed on every save. Each tab here owns its own endpoints, and the
 * programme PATCH touches none of them.
 *
 * The drawer is KEYED to the programme id by its caller. Without that, opening
 * B while A is mounted reuses A's tab state and its in-flight requests, and the
 * drawer can show one programme's milestones under another's name.
 */

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'targeting', label: 'Targeting' },
  { id: 'beneficiaries', label: 'Beneficiaries' },
  { id: 'funnel', label: 'Funnel' },
  { id: 'money', label: 'Money' },
  { id: 'impact', label: 'Impact' },
  { id: 'milestones', label: 'Milestones' },
  { id: 'kpis', label: 'KPIs' },
  { id: 'documents', label: 'Documents' },
  { id: 'partners', label: 'Partners' },
  { id: 'connections', label: 'Connections' },
  // Its own tab rather than a section on Connections: a coverage panel that is
  // empty for most programmes still has to be findable for the ones it is not,
  // and burying it under a tab about partner organisations makes it neither.
  { id: 'coverage', label: 'Coverage' },
  { id: 'audit', label: 'Audit' },
] as const

type TabId = typeof TABS[number]['id']

export function ProgramDrawer({ programId, onClose, onChanged }: {
  programId: string
  onClose: () => void
  onChanged?: () => void
}) {
  const canUpdate = useCan('program:update')
  const canCancel = useCan('program:cancel')

  const [program, setProgram] = useState<ProgramResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<TabId>('overview')
  const [editing, setEditing] = useState(false)
  const [statusBusy, setStatusBusy] = useState(false)
  const [statusError, setStatusError] = useState<string | null>(null)

  const load = () => {
    getProgram(programId).then(setProgram).catch((e) => setError(explain(e)))
  }

  useEffect(() => {
    let alive = true
    getProgram(programId)
      .then((p) => { if (alive) setProgram(p) })
      .catch((e) => { if (alive) setError(explain(e)) })
    return () => { alive = false }
  }, [programId])

  const transitions = program ? STATUS_TRANSITIONS[program.statusCode] ?? [] : []

  const move = async (to: string) => {
    if (!program) return
    // Cancelling is destructive-adjacent: the row survives, but the programme
    // stops. A confirm here matches the weight of the action; every other
    // transition is reversible or terminal-by-completion.
    if (to === 'CANCELLED') {
      const reason = window.prompt('Why is this programme being cancelled?')
      if (reason === null) return
      setStatusBusy(true); setStatusError(null)
      try {
        setProgram(await changeProgramStatus(program.id, to, reason))
        onChanged?.()
      } catch (e) { setStatusError(explain(e)) } finally { setStatusBusy(false) }
      return
    }
    setStatusBusy(true); setStatusError(null)
    try {
      setProgram(await changeProgramStatus(program.id, to))
      onChanged?.()
    } catch (e) { setStatusError(explain(e)) } finally { setStatusBusy(false) }
  }

  return (
    <Drawer
      open
      onClose={onClose}
      width="lg"
      title={program?.name ?? 'Programme'}
      subtitle={program ? (
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-erp-xs text-muted-foreground">{program.code}</span>
          <ProgramStatusBadge code={program.statusCode} />
          <CategoryBadge code={program.categoryCode} />
          {program.scopeUnitPath === null && (
            <span className="text-erp-xs text-muted-foreground">National</span>
          )}
        </span>
      ) : undefined}
      right={program && canUpdate ? (
        <button type="button" className="erp-btn h-8" onClick={() => setEditing(true)}>
          <Pencil className="h-3.5 w-3.5" /> Edit
        </button>
      ) : undefined}
    >
      <ErrorStrip message={error} />
      {!program ? <LoadingBlock label="Loading programme…" /> : (
        <>
          {/* Tabs. `role="tab"` rather than a button, so assistive tech and the
              Playwright suite both find them by what they are. */}
          {/* A tablist is a ROVING focus: one tab stop for the whole strip, and
              the arrows move between tabs. Twelve tabs each taking their own tab
              stop puts eleven presses between the strip and the panel, which is
              why the pattern exists. */}
          <div className="flex flex-wrap gap-1 border-b border-border pb-2 mb-3" role="tablist"
            aria-label="Programme detail">
            {TABS.map((t, i) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                id={`program-tab-${t.id}`}
                // Only the SELECTED tab points at a panel: one panel is mounted
                // at a time, so eleven of these would otherwise reference an id
                // that is not in the document.
                aria-controls={tab === t.id ? 'program-panel' : undefined}
                aria-selected={tab === t.id}
                tabIndex={tab === t.id ? 0 : -1}
                onKeyDown={(e) => {
                  if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
                  e.preventDefault()
                  const step = e.key === 'ArrowRight' ? 1 : -1
                  const next = TABS[(i + step + TABS.length) % TABS.length]!
                  setTab(next.id)
                  document.getElementById(`program-tab-${next.id}`)?.focus()
                }}
                className={cn(
                  'px-2.5 py-1 rounded-md text-erp-xs font-medium',
                  tab === t.id
                    ? 'bg-chocolate-soft/60 text-chocolate'
                    : 'text-muted-foreground hover:bg-muted',
                )}
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* `tabIndex={0}` because a roving tablist leaves the panel unreachable
              otherwise whenever its content holds nothing focusable — which is
              most of the read-only tabs. */}
          <div role="tabpanel" id="program-panel" tabIndex={0}
            aria-labelledby={`program-tab-${tab}`}
            className="focus:outline-none">
          {tab === 'overview' && (
            <div className="space-y-4">
              <DrawerSection title="Status">
                <ErrorStrip message={statusError} />
                {transitions.length === 0 ? (
                  <Note tone="info">
                    {program.statusCode === 'COMPLETED' || program.statusCode === 'CANCELLED'
                      ? `A ${programStatusLabel(program.statusCode).toLowerCase()} programme is terminal — it cannot move again.`
                      : 'No transitions available.'}
                  </Note>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {transitions.map((to) => {
                      // Only offer what the server accepts, so a user never
                      // meets a 409 naming two states they did not choose.
                      const needsCancel = to === 'CANCELLED'
                      const blocked = needsCancel && !canCancel
                      const noRule = to === 'ACTIVE' && !program.activeRuleVersion
                      return (
                        <button
                          key={to}
                          type="button"
                          className="erp-btn h-8"
                          disabled={statusBusy || !canUpdate || blocked || noRule}
                          title={blocked
                            ? 'Cancelling a programme needs the program:cancel authority.'
                            : noRule
                              ? 'Publish a targeting rule version first — until then the eligible pool is undefined.'
                              : undefined}
                          onClick={() => move(to)}
                        >
                          Move to {programStatusLabel(to)}
                        </button>
                      )
                    })}
                  </div>
                )}
                {program.statusCode === 'DRAFT' && !program.activeRuleVersion && (
                  <Note tone="info">
                    This programme has no published targeting. Until it does, its eligible pool is
                    undefined and it cannot be activated.
                  </Note>
                )}
                {program.cancellationReason && (
                  <Note tone="bad">Cancelled: {program.cancellationReason}</Note>
                )}
              </DrawerSection>

              <DrawerSection title="Details">
                <DrawerField label="Delivery channel" value={<><ChannelBadge code={program.deliveryChannelCode} /></>} />
                <DrawerField label="Runs" value={<>{fmtDate(program.startsOn)}{program.endsOn ? ` → ${fmtDate(program.endsOn)}` : ' → open-ended'}</>} />
                <DrawerField label="Scope" value={<>{program.scopeUnitPath ?? 'National — visible to every user at every level'}</>} />
                <DrawerField label="Lead partner" value={<>{program.partnerName ?? '—'}</>} />
                <DrawerField label="Description" value={<>{program.description ?? '—'}</>} />
              </DrawerSection>

              <DrawerSection title="Money">
                <DrawerField label="Budget" value={<><Money value={program.budgetAmount} currency={program.currency} /></>} />
                <DrawerField label="Spent" value={<><Money value={program.spentAmount} currency={program.currency} />
                  <span className="ml-2 text-erp-xs text-muted-foreground">
                    derived from the ledger
                  </span></>} />
                <DrawerField label="Remaining" value={<>{/* NULL, never 0. A programme with no budget has no remaining
                      budget, and rendering 0 invites "fully spent". */}
                  {program.budgetAmount === null || program.budgetAmount === undefined
                    ? <span className="text-muted-foreground">— no budget recorded</span>
                    : <Money
                        value={Number(program.budgetAmount) - Number(program.spentAmount ?? 0)}
                        currency={program.currency}
                      />}</>} />
                <DrawerField label="Last reconciled" value={<>{program.spendReconciledAt ? fmtDateTime(program.spendReconciledAt) : '—'}
                  {Number(program.spendDiscrepancy ?? 0) !== 0 && (
                    <span className="ml-2 text-erp-xs text-red-700 dark:text-red-300">
                      discrepancy <Money value={program.spendDiscrepancy} currency={program.currency} precise />
                    </span>
                  )}</>} />
              </DrawerSection>

              <DrawerSection title="Plan versus result">
                <DrawerField label="Planned uplift" value={<>{program.expectedUpliftPp === null || program.expectedUpliftPp === undefined
                    ? <span className="text-muted-foreground">— none recorded</span>
                    : <PlannedChip value={program.expectedUpliftPp} />}</>} />
                <DrawerField label="Impact last measured" value={<>{program.impactMeasuredAt ? fmtDateTime(program.impactMeasuredAt) : '— never'}</>} />
                <p className="text-erp-xs text-muted-foreground">
                  The planned figure is what somebody expected when the programme was designed.
                  It is never added to, subtracted from, or substituted for a measured result —
                  see the Impact tab for what was actually observed.
                </p>
              </DrawerSection>
            </div>
          )}

          {tab === 'targeting' && <TargetingPanel program={program} onChanged={() => { load(); onChanged?.() }} />}
          {tab === 'beneficiaries' && <BeneficiariesPanel program={program} />}
          {tab === 'funnel' && <FunnelPanel program={program} />}
          {tab === 'money' && <MoneyPanel program={program} onChanged={load} />}
          {tab === 'impact' && <ImpactPanel program={program} />}
          {tab === 'milestones' && <MilestonesPanel program={program} />}
          {tab === 'kpis' && <KpisPanel program={program} />}
          {tab === 'documents' && <DocumentsPanel program={program} />}
          {tab === 'partners' && <PartnersPanel program={program} />}
          {tab === 'connections' && <ConnectionsPanel program={program} />}
          {tab === 'coverage' && <ProgramCoveragePanel program={program} />}
          {tab === 'audit' && <ProgramAuditPanel program={program} />}
          </div>
        </>
      )}

      {editing && program && (
        <ProgramFormDialog
          existing={program}
          onClose={() => setEditing(false)}
          onSaved={(p) => { setEditing(false); setProgram(p); onChanged?.() }}
        />
      )}
    </Drawer>
  )
}

export const ProgramRefreshIcon = RefreshCw
